import React, { useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { Loading, TextArea } from 'components'
import { translate } from 'locales'
import { alertDanger, alertSuccess } from 'utils/toast'
import { ReactComponent as DissatisfiedIcon } from 'assets/icons/dissatisfied.svg'
import { ReactComponent as NeutralIcon } from 'assets/icons/neutral.svg'
import { ReactComponent as SatisfiedIcon } from 'assets/icons/satisfied.svg'
import type { ModalComponentProps } from 'modal/types'
import videoCallRatingAnalytics from 'services/analytics/VideoCallRating/videoCallRatingAnalytics'
import { getCurrentDoctorId } from 'ui/ProfessionalNPS/services'
import type { VideoCallEmotionFeaturesResponse, VideoCallSentiment } from './services'
import {
  fetchAppointmentCsatEvaluation,
  fetchVideoCallEmotionFeatures,
  markVideoCallRatingSubmitted,
  sentimentToStars,
  submitVideoCallRating,
} from './services'
import * as S from './styles'

export type VideoCallRatingModalOwnProps = Record<string, unknown> & {
  schedulingId: string
  initialSentiment?: VideoCallSentiment | null
  source?: 'call_end' | 'side_prompt' | 'unknown'
}

export type VideoCallRatingModalProps = ModalComponentProps<
  VideoCallRatingModalOwnProps
>

const schema = yup.object({
  sentiment: yup
    .string()
    .oneOf(['negative', 'neutral', 'positive'] as const)
    .required(),
  typeVideoCallIds: yup.array().of(yup.string().required()),
  comment: yup.string().max(5000).notRequired(),
})

function CloseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M15 5L5 15M5 5l10 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function VideoCallRatingModal({
  onClose,
  schedulingId,
  initialSentiment = null,
  source = 'unknown',
}: VideoCallRatingModalProps) {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const [gatePassed, setGatePassed] = useState(false)
  const [emotionFeatures, setEmotionFeatures] =
    useState<VideoCallEmotionFeaturesResponse | null>(null)
  const [featuresLoading, setFeaturesLoading] = useState(false)

  const formik = useFormik({
    initialValues: {
      sentiment: initialSentiment as VideoCallSentiment | null,
      typeVideoCallIds: [] as string[],
      comment: '',
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      if (!values.sentiment) {
        alertDanger(translate('videoCallRating.errors.selectSentiment'))
        return
      }

      if (!getCurrentDoctorId()) {
        alertDanger(translate('videoCallRating.errors.submitFailed'))
        return
      }

      try {
        videoCallRatingAnalytics.trackSubmit({
          appointmentId: schedulingId,
          sentiment: values.sentiment,
          selectedReasonsCount: values.typeVideoCallIds.length,
          hasComment: values.comment.trim().length > 0,
        })
        await submitVideoCallRating({
          appointmentId: schedulingId,
          sentiment: values.sentiment,
          typeVideoCallIds: values.typeVideoCallIds,
          comment: values.comment,
        })
        markVideoCallRatingSubmitted(schedulingId)
        videoCallRatingAnalytics.trackSubmitSuccess(schedulingId)
        alertSuccess(translate('videoCallRating.success'))
        onClose()
      } catch {
        videoCallRatingAnalytics.trackSubmitFailed(schedulingId)
        alertDanger(translate('videoCallRating.errors.submitFailed'))
      }
    },
  })

  useEffect(() => {
    videoCallRatingAnalytics.trackModalView({
      appointmentId: schedulingId,
      source,
      hasInitialSentiment: Boolean(initialSentiment),
    })
  }, [schedulingId, source, initialSentiment])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { evaluation } = await fetchAppointmentCsatEvaluation(schedulingId)
        if (cancelled) return
        if (evaluation) {
          markVideoCallRatingSubmitted(schedulingId)
          onCloseRef.current()
          return
        }
        setGatePassed(true)
      } catch {
        if (!cancelled) setGatePassed(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [schedulingId])

  useEffect(() => {
    if (!gatePassed || !formik.values.sentiment) {
      setEmotionFeatures(null)
      return
    }

    const stars = sentimentToStars(formik.values.sentiment)
    let cancelled = false
    setFeaturesLoading(true)
    setEmotionFeatures(null)

    fetchVideoCallEmotionFeatures(stars)
      .then((data) => {
        if (cancelled) return
        setEmotionFeatures(data)
        formik.setFieldValue('typeVideoCallIds', [])
      })
      .catch(() => {
        if (cancelled) return
        alertDanger(translate('videoCallRating.errors.loadOptionsFailed'))
      })
      .finally(() => {
        if (!cancelled) setFeaturesLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [gatePassed, formik.values.sentiment])

  const selectSentiment = (s: VideoCallSentiment) => {
    formik.setFieldValue('sentiment', s)
    formik.setFieldValue('typeVideoCallIds', [])
  }

  const toggleOption = (optionId: string) => {
    const next = formik.values.typeVideoCallIds.includes(optionId)
      ? formik.values.typeVideoCallIds.filter((t) => t !== optionId)
      : [...formik.values.typeVideoCallIds, optionId]
    formik.setFieldValue('typeVideoCallIds', next)
  }

  const sentimentSelected = formik.values.sentiment !== null
  const options = emotionFeatures?.options ?? []

  if (!gatePassed) {
    return (
      <S.ModalWrapper data-testid="video-call-rating-modal-loading">
        <Loading title={translate('videoCallRating.loading')} />
      </S.ModalWrapper>
    )
  }

  return (
    <S.ModalWrapper data-testid="video-call-rating-modal">
      <S.HeaderRow data-testid="video-call-rating-header">
        <S.Title data-testid="video-call-rating-title">
          {translate('videoCallRating.title')}
        </S.Title>
        <S.CloseButton
          type="button"
          onClick={() => {
            videoCallRatingAnalytics.trackClose(schedulingId, 'close_button')
            onClose()
          }}
          aria-label={translate('videoCallRating.actions.close')}
          data-testid="video-call-rating-close"
        >
          <CloseIcon />
        </S.CloseButton>
      </S.HeaderRow>

      <S.Form onSubmit={formik.handleSubmit} data-testid="video-call-rating-form">
        <div data-testid="video-call-rating-question-block">
          <S.Question data-testid="video-call-rating-question">
            {translate('videoCallRating.question')}
          </S.Question>
          <S.FacesRow data-testid="video-call-rating-faces">
            <S.FaceButton
              type="button"
              $variant="negative"
              $selected={formik.values.sentiment === 'negative'}
              onClick={() => selectSentiment('negative')}
              aria-pressed={formik.values.sentiment === 'negative'}
              aria-label={translate('videoCallRating.sentiments.negative')}
              data-testid="video-call-rating-sentiment-negative"
            >
              <DissatisfiedIcon width={36} height={36} aria-hidden />
            </S.FaceButton>
            <S.FaceButton
              type="button"
              $variant="neutral"
              $selected={formik.values.sentiment === 'neutral'}
              onClick={() => selectSentiment('neutral')}
              aria-pressed={formik.values.sentiment === 'neutral'}
              aria-label={translate('videoCallRating.sentiments.neutral')}
              data-testid="video-call-rating-sentiment-neutral"
            >
              <NeutralIcon width={36} height={36} aria-hidden />
            </S.FaceButton>
            <S.FaceButton
              type="button"
              $variant="positive"
              $selected={formik.values.sentiment === 'positive'}
              onClick={() => selectSentiment('positive')}
              aria-pressed={formik.values.sentiment === 'positive'}
              aria-label={translate('videoCallRating.sentiments.positive')}
              data-testid="video-call-rating-sentiment-positive"
            >
              <SatisfiedIcon width={36} height={36} aria-hidden />
            </S.FaceButton>
          </S.FacesRow>
        </div>

        {sentimentSelected && (
          <div data-testid="video-call-rating-tags-section">
            {featuresLoading && (
              <p style={{ margin: 0, textAlign: 'center', fontSize: '1.4rem' }}>
                {translate('videoCallRating.loadingOptions')}
              </p>
            )}
            {!featuresLoading && emotionFeatures?.title && (
              <S.SectionLabel data-testid="video-call-rating-tags-label">
                {emotionFeatures.title}
              </S.SectionLabel>
            )}
            {!featuresLoading && options.length > 0 && (
              <S.ChipsWrap data-testid="video-call-rating-chips">
                {options.map((opt) => (
                  <S.Chip
                    key={opt.id}
                    type="button"
                    $selected={formik.values.typeVideoCallIds.includes(opt.id)}
                    onClick={() => toggleOption(opt.id)}
                    data-testid={`video-call-rating-chip-${opt.id}`}
                  >
                    {opt.name}
                  </S.Chip>
                ))}
              </S.ChipsWrap>
            )}
          </div>
        )}
       
        {sentimentSelected && (
          <S.TextareaWrap data-testid="video-call-rating-comment-wrap">
            <TextArea
              name="comment"
              placeholder={translate('videoCallRating.commentPlaceholder')}
              value={formik.values.comment}
              onChange={formik.handleChange}
              rows={4}
            />
          </S.TextareaWrap>
        )}

        <S.SubmitButton
          type="submit"
          disabled={
            !sentimentSelected || formik.isSubmitting || featuresLoading
          }
          data-testid="video-call-rating-submit"
        >
          {translate('videoCallRating.actions.submit')}
        </S.SubmitButton>
      </S.Form>
    </S.ModalWrapper>
  )
}

VideoCallRatingModal.displayName = 'VideoCallRating'
